import type { ActivityDto } from '@kanban/shared';
import { useState } from 'react';
import { describeActivity } from '../lib/activity.js';
import {
  useBoardActivity,
  useBoardArchive,
  useRestoreCard,
  useRestoreColumn,
} from '../lib/boards.js';
import { formatDateTime, relativeTime } from '../lib/format.js';

/**
 * Archivansicht eines Boards (SPEC 9.6, bewusst auf das Noetige beschraenkt).
 *
 * Sie kommt mit dem Papierkorb zusammen, nicht spaeter: eine Geste, die das
 * Archivieren auf eine Fingerbewegung verkuerzt, braucht einen Weg zurueck,
 * der laenger haelt als die acht Sekunden des Rueckgaengig-Hinweises.
 *
 * Der Board-Verlauf liegt im zweiten Reiter (SPEC 9.6). Er gehoert hierher und
 * nicht auf eine eigene Seite: „was ist mit der Karte passiert" und „wo ist
 * sie hin" sind dieselbe Frage, nur unterschiedlich weit fortgeschritten.
 */
export function ArchiveSheet({ boardId, onClose }: { boardId: string; onClose: () => void }) {
  const [tab, setTab] = useState<'archiv' | 'verlauf'>('archiv');
  // Jeweils nur das geladen, was sichtbar ist.
  const archive = useBoardArchive(boardId, tab === 'archiv');
  const activity = useBoardActivity(boardId, tab === 'verlauf');
  const restoreCard = useRestoreCard(boardId);
  const restoreColumn = useRestoreColumn(boardId);

  const data = archive.data;
  const isEmpty = data !== undefined && data.columns.length === 0 && data.cards.length === 0;

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-[rgb(0_0_0/45%)] md:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Archiv und Verlauf"
      data-testid="archive-sheet"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[80vh] w-full flex-col rounded-t-[var(--radius-lg)] bg-surface shadow-[var(--shadow-3)] md:max-w-md md:rounded-[var(--radius-lg)]">
        <div className="flex items-center justify-between px-4 py-3">
          <h2 className="text-sm font-semibold">Archiv</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Archiv schließen"
            className="text-sm text-muted"
            style={{ minWidth: 'var(--touch-target)', minHeight: 'var(--touch-target)' }}
          >
            ✕
          </button>
        </div>

        <div className="flex gap-2 px-4 pb-2" role="tablist">
          {(['archiv', 'verlauf'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              data-testid={`archive-tab-${value}`}
              onClick={() => setTab(value)}
              className={`rounded-[var(--radius-sm)] border px-3 py-1 text-xs ${
                tab === value ? 'border-accent text-accent' : 'border-border text-muted'
              }`}
            >
              {value === 'archiv' ? 'Archiviert' : 'Verlauf'}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
          {tab === 'verlauf' && <BoardActivity entries={activity.data} isPending={activity.isPending} />}
          {tab === 'archiv' && archive.isPending && (
            <p className="py-6 text-center text-sm text-muted">Lädt …</p>
          )}
          {tab === 'archiv' && archive.isError && (
            <p className="py-6 text-center text-sm text-danger">Archiv nicht erreichbar</p>
          )}
          {tab === 'archiv' && isEmpty && (
            <p className="py-6 text-center text-sm text-muted">Nichts archiviert.</p>
          )}

          {tab === 'archiv' && data && data.columns.length > 0 && (
            <section>
              <h3 className="mb-2 text-xs font-medium text-muted">Spalten</h3>
              <ul className="space-y-2">
                {data.columns.map((column) => (
                  <li
                    key={column.id}
                    data-testid="archived-column"
                    className="flex items-center gap-3 rounded-[var(--radius)] border border-border px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{column.title}</p>
                      <p className="text-xs text-muted">
                        {column.cardCount === 1 ? '1 Karte' : `${column.cardCount} Karten`} ·{' '}
                        {formatDateTime(column.archivedAt)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => restoreColumn.mutate(column.id)}
                      className="shrink-0 rounded-[var(--radius-sm)] border border-border px-3 py-1 text-xs"
                    >
                      Zurückholen
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {tab === 'archiv' && data && data.cards.length > 0 && (
            <section className="mt-4">
              <h3 className="mb-2 text-xs font-medium text-muted">Karten</h3>
              <ul className="space-y-2">
                {data.cards.map((card) => (
                  <li
                    key={card.id}
                    data-testid="archived-card"
                    className="flex items-center gap-3 rounded-[var(--radius)] border border-border px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{card.title}</p>
                      <p className="text-xs text-muted">
                        {card.columnTitle} · {formatDateTime(card.archivedAt)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => restoreCard.mutate(card.id)}
                      className="shrink-0 rounded-[var(--radius-sm)] border border-border px-3 py-1 text-xs"
                    >
                      Zurückholen
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Board-Verlauf: was zuletzt passiert ist, ueber alle Karten hinweg.
 *
 * Eintraege, deren Typ dieser Stand des Codes nicht kennt, fallen weg statt
 * als Rohdaten dazustehen — ein Verlauf mit `card.teleported` darin waere
 * schlechter als einer ohne.
 */
function BoardActivity({
  entries,
  isPending,
}: {
  entries: ActivityDto[] | undefined;
  isPending: boolean;
}) {
  if (isPending) return <p className="py-6 text-center text-sm text-muted">Lädt …</p>;

  const rows = (entries ?? [])
    .map((entry) => ({ entry, text: describeActivity(entry) }))
    .filter((row): row is { entry: ActivityDto; text: string } => row.text !== null);

  if (rows.length === 0) {
    return <p className="py-6 text-center text-sm text-muted">Noch nichts passiert.</p>;
  }

  return (
    <ul className="space-y-2" data-testid="board-activity">
      {rows.map(({ entry, text }) => (
        <li key={entry.id} className="text-sm" data-testid="board-activity-entry">
          <span className="font-medium">{entry.actor.name}</span> {text}
          {entry.cardTitle && <span className="text-muted"> ({entry.cardTitle})</span>}
          <span className="text-muted"> · {relativeTime(entry.createdAt)}</span>
        </li>
      ))}
    </ul>
  );
}
